<script lang="ts">
  import { onDestroy, onMount } from 'svelte'
  import { SvelteMap } from 'svelte/reactivity'
  import { t } from '../lib/i18n.svelte'
  import { MarkBoard } from '../lib/marks'
  import { activeStream, onHostEvent, onMarkEvent, onMarksMode } from '../lib/tauri'

  /**
   * The host's mark overlay: a transparent, click-through window laid over the
   * shared screen by the desktop shell and kept out of the capture. It paints
   * every viewer's pointer, ping and ink, and the host's own, from fractions of
   * the shared frame, so the window is just a full-size canvas.
   *
   * The shell hides the window when nothing is live; this page only has to stop
   * animating then.
   */
  const board = new MarkBoard()
  const names = new SvelteMap<string, string>()
  let canvas = $state<HTMLCanvasElement | null>(null)
  let frame: number | null = null
  let marking = $state(false)
  let unlisten: (() => void)[] = []

  function paint() {
    frame = null
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const ratio = window.devicePixelRatio || 1
    const width = Math.max(1, Math.round(canvas.clientWidth * ratio))
    const height = Math.max(1, Math.round(canvas.clientHeight * ratio))
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    }
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    const now = performance.now()
    const live = board.prune(now)
    board.draw(ctx, { x: 0, y: 0, w: canvas.clientWidth, h: canvas.clientHeight }, now, (peer) =>
      names.get(peer),
    )
    if (live) frame = requestAnimationFrame(paint)
  }

  function wake() {
    if (frame === null) frame = requestAnimationFrame(paint)
  }

  onMount(async () => {
    document.documentElement.classList.add('overlay-window')
    unlisten = await Promise.all([
      onMarkEvent((mark) => {
        board.apply(mark, performance.now())
        wake()
      }),
      onMarksMode((on) => (marking = on)),
      // Who is who, for the label next to a pointer.
      onHostEvent((event) => {
        if (event.type === 'viewer_joined') names.set(event.peer_id, event.name)
        else if (event.type === 'viewer_left') {
          names.delete(event.peer_id)
          board.drop(event.peer_id)
        }
      }),
    ])
    const snapshot = await activeStream().catch(() => null)
    for (const event of snapshot?.events ?? []) {
      if (event.type === 'viewer_joined') names.set(event.peer_id, event.name)
    }
  })

  onDestroy(() => {
    unlisten.forEach((off) => off())
    if (frame !== null) cancelAnimationFrame(frame)
  })
</script>

<svelte:window onresize={wake} />

<canvas bind:this={canvas} class="host-marks" aria-hidden="true"></canvas>
{#if marking}
  <div class="host-marks__mode">
    <kbd>D</kbd>
    {t('draw')}
    <kbd>L</kbd>
    {t('pointer')}
    <kbd>P</kbd>
    {t('ping')}
    <span>{t('Hold D or L. Ctrl+Shift+M to stop.')}</span>
  </div>
{/if}
